import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, AlertCircle, MapPin, Phone, Users, Briefcase, Clock, Settings, FileText } from 'lucide-react';
import { dataQualityApi } from '../services/dataQualityApi';
import type { DataQualityCategoryDto, DataQualityCheckDto } from '../types/dataQuality';
import './DataQualityDashboardPage.css';

type BreakdownFilter = 'needs-attention' | 'all';

const categoryIcons: Record<string, React.ReactElement> = {
  location: <MapPin size={24} />,
  communication: <Phone size={24} />,
  contacts: <Users size={24} />,
  services: <Briefcase size={24} />,
  hours: <Clock size={24} />,
  capabilities: <Settings size={24} />,
  onboarding: <FileText size={24} />,
};

function getHighestSeverity(checks: DataQualityCheckDto[]): string {
  if (checks.some(c => c.severity === 'critical')) return 'critical';
  if (checks.some(c => c.severity === 'warning')) return 'warning';
  return 'info';
}

function getFirstCheckId(category: DataQualityCategoryDto): string {
  if (category.checks.length === 0) return '';
  const sorted = [...category.checks].sort((a, b) => b.count - a.count);
  return sorted[0].checkId;
}

export default function DataQualityDashboardPage() {
  const navigate = useNavigate();
  const [breakdownFilter, setBreakdownFilter] = useState<BreakdownFilter>('needs-attention');

  const { data, isLoading, error } = useQuery({
    queryKey: ['data-quality-summary'],
    queryFn: () => dataQualityApi.getSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const severityRank: Record<string, number> = { critical: 0, warning: 1, info: 2 };
  const breakdownRows = data
    ? data.categories
        .flatMap((cat) =>
          cat.checks
            .filter((check) => breakdownFilter === 'all' || (check.count ?? 0) > 0)
            .map((check) => ({ cat, check })),
        )
        .sort((a, b) => {
          const countDiff = (b.check.count ?? 0) - (a.check.count ?? 0);
          if (countDiff !== 0) return countDiff;
          const sevDiff = (severityRank[a.check.severity] ?? 99) - (severityRank[b.check.severity] ?? 99);
          if (sevDiff !== 0) return sevDiff;
          return a.check.label.localeCompare(b.check.label);
        })
    : [];

  return (
    <div className="dq-page">
      <div className="page-header">
        <div className="page-title-row">
          <ShieldAlert size={28} />
          <h1>Clinic Data Quality</h1>
        </div>
        <p className="page-subtitle">
          Overview of data completeness across all active clinics. Click any issue to see affected clinics.
        </p>
      </div>

      {isLoading && (
        <div className="dq-loading-state">
          <div className="spinner" />
          <p>Loading data quality summary...</p>
        </div>
      )}

      {error && (
        <div className="dq-error-state">
          <AlertCircle size={24} />
          <p>Failed to load data quality summary. Please try again.</p>
        </div>
      )}

      {data && data.categories.length === 0 && (
        <div className="dq-empty-state">
          <ShieldAlert size={48} />
          <h3>No data quality issues found</h3>
          <p>All active clinics have complete data.</p>
        </div>
      )}

      {data && data.categories.length > 0 && (
        <>
          <div className="dq-health-section">
            <div className="dq-health-bar-container">
              <div className="dq-health-label">
                <span>Overall Completeness</span>
                <span className="dq-health-percent">
                  {data.overallCompletenessPercent.toFixed(1)}%
                </span>
              </div>
              <div className="dq-health-bar">
                <div
                  className="dq-health-bar-fill"
                  style={{ width: `${data.overallCompletenessPercent}%` }}
                />
              </div>
            </div>
            <div className="dq-total-clinics">
              {data.totalActiveClinics.toLocaleString()} active clinics analyzed
            </div>
          </div>

          <div className="dq-category-grid">
            {data.categories.map((category) => {
              const highestSeverity = getHighestSeverity(category.checks);
              const firstCheckId = getFirstCheckId(category);
              const icon = categoryIcons[category.category] || <ShieldAlert size={24} />;

              return (
                <div
                  key={category.category}
                  className={`dq-category-card dq-severity-${highestSeverity}`}
                  onClick={() => navigate(`/data-quality/${firstCheckId}`)}
                >
                  <div className="dq-category-icon">{icon}</div>
                  <div className="dq-category-info">
                    <div className="dq-category-name">{category.label}</div>
                    <div className="dq-category-count">
                      {category.totalIssues.toLocaleString()}
                    </div>
                    <div className="dq-category-sublabel">issues</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="dq-breakdown-section">
            <div className="dq-breakdown-header">
              <h2>Detailed Breakdown</h2>
              <div className="dq-filter-toggle" aria-label="Breakdown filter">
                <button
                  type="button"
                  aria-pressed={breakdownFilter === 'needs-attention'}
                  className={`dq-filter-toggle-btn ${breakdownFilter === 'needs-attention' ? 'is-active' : ''}`}
                  onClick={() => setBreakdownFilter('needs-attention')}
                >
                  Needs Attention
                </button>
                <button
                  type="button"
                  aria-pressed={breakdownFilter === 'all'}
                  className={`dq-filter-toggle-btn ${breakdownFilter === 'all' ? 'is-active' : ''}`}
                  onClick={() => setBreakdownFilter('all')}
                >
                  View All
                </button>
              </div>
            </div>
            <div className="dq-table-container">
              <table className="dq-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Check</th>
                    <th>Count</th>
                    <th>% of Active</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="dq-table-empty">
                        No checks currently need attention.
                      </td>
                    </tr>
                  ) : (
                    breakdownRows.map(({ cat, check }) => (
                      <tr
                        key={check.checkId}
                        className="dq-table-row-clickable"
                        onClick={() => navigate(`/data-quality/${check.checkId}`)}
                      >
                        <td>{cat.label}</td>
                        <td>{check.label}</td>
                        <td className="dq-count-cell">
                          {check.count.toLocaleString()}
                        </td>
                        <td>{check.percentOfActive.toFixed(1)}%</td>
                        <td>
                          <span
                            className={`dq-severity-badge dq-severity-${check.severity}`}
                          >
                            {check.severity}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
